import React, { useState } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BookOpen, Calendar, CheckCircle, Clock, Download, Eye, FileText, Link2, UserPlus, Users } from 'lucide-react';
import InlineEdit from '@/components/InlineEdit';
import StudentSubmitBox from './StudentSubmitBox';
import { isVideoFile, safeDownload, formatScheduledDate } from '@/components/shared/contentHelpers';
import { PdfActivityDialog } from '@/components/PdfActivityDialog';
import DeleteContentMenu from '@/components/shared/DeleteContentMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContentItemCard({
  item, contentType, isStudent, token, t,
  downloadLabel, submitLabel, showExternalCompletion, reviewLabel,
  onUpdated, onOpenSchedule, onReview, onDelete, onReuse,
  classStudents = [],
}) {
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  // Fase 5 · Detectar los PDFs "activity" del item. Reusamos exactamente el
  // mismo criterio que LessonCard: `file_type === 'activity'` o, para items
  // legacy sin file_type, cualquier fichero que NO sea vídeo. Cuando el item
  // está enlazado a Feedback Hub (`bank_id` presente) usamos el mismo
  // PdfActivityDialog que ya funciona en Lecciones.
  const activityFiles = (item.files || []).filter(
    f => f.file_type === 'activity' || (!f.file_type && !isVideoFile(f.filename))
  );
  const hasBank = Boolean(item.bank_id);
  const hasActivityPdf = activityFiles.length > 0;
  const [openActivityFile, setOpenActivityFile] = useState(null);

  const saveInstructions = async (text) => {
    try {
      await axios.patch(`${API}/content/instructions/${item.id}`, { description: text }, headers);
      onUpdated(item.id, { description: text });
      toast.success(t('success'));
    } catch (err) {
      toast.error(t('error'));
      throw err;
    }
  };

  return (
    <div className="content-card bg-white rounded-xl p-5" data-testid={`${contentType}-${item.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-bold text-slate-800">{item.title}</h4>
          <div className="mt-1">
            <InlineEdit
              value={item.description || ''}
              canEdit={!isStudent}
              placeholder="Sin instrucciones"
              onSave={saveInstructions}
            />
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {!isStudent && item.scheduled_for && !item.is_published && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                <Clock className="w-3 h-3 mr-1" />
                {t('scheduled_for')}: {formatScheduledDate(item.scheduled_for)}
              </Badge>
            )}
            {!isStudent && item.assigned_to && item.assigned_to.length > 0 && (
              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">
                <Users className="w-3 h-3 mr-1" />{item.assigned_to.length} estudiante(s)
              </Badge>
            )}
            {item.files && item.files.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                <FileText className="w-3 h-3 mr-1" />{item.files.length} archivo(s)
              </Badge>
            )}
            <Badge variant="outline" className="text-xs text-slate-400">
              {new Date(item.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isStudent && (
            <button onClick={() => onReuse(item)}
              className="text-slate-300 hover:text-emerald-500 transition-colors"
              title="Asignar a otros estudiantes"
              data-testid={`reuse-${item.id}`}>
              <UserPlus className="w-4 h-4" />
            </button>
          )}
          {!isStudent && (
            <button onClick={() => onOpenSchedule(item)}
              className="text-slate-300 hover:text-blue-500 transition-colors"
              title={t('edit_schedule')}
              data-testid={`edit-schedule-${item.id}`}>
              <Calendar className="w-4 h-4" />
            </button>
          )}
          {!isStudent && (
            <DeleteContentMenu
              contentId={item.id}
              classStudents={classStudents}
              token={token}
              onDelete={onDelete}
              onUnassigned={() => onUpdated(item.id, null)}
              testIdPrefix={`delete-${contentType}`}
            />
          )}
        </div>
      </div>

      {/* Teacher's files (1-click Blob download).
          Fase 2 · The student's «Correcciones» tab must not surface a
          "Descargar corrección" button — students must work exclusively
          inside the PDF viewer via StudentCorrectionsSection. All other
          content types keep the download list intact (tasks, reinforcement,
          lessons without a bank, etc.).
          Fase 5 · Además, cuando el item está enlazado a Feedback Hub
          (`bank_id` presente) ocultamos la lista de descarga para el
          alumno en Tareas y Refuerzo Académico — el alumno debe abrir la
          actividad interactiva en su lugar. Mismo criterio que ya usa
          LessonCard.js para las lecciones. El docente sigue viendo los
          archivos para su gestión. */}
      {item.files?.length > 0
        && !(isStudent && contentType === 'correction')
        && !(isStudent && hasBank) && (
        <div className="space-y-2 mb-3">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{downloadLabel}</p>
          {item.files.map((f, i) => (
            <button key={f.filename || `f-${i}`} onClick={() => safeDownload(f.filename, f.original_name, token, t)}
              className="w-full flex items-center gap-2 p-2.5 bg-blue-50 rounded-lg hover:bg-blue-100 text-sm text-blue-700 transition-colors text-left"
              data-testid={`download-${contentType}-${item.id}-${i}`}>
              <Download className="w-4 h-4" /><span className="font-medium">{f.original_name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Fase 5 · Admin/docente: enlazar el content con un Answer Bank de
          Feedback Hub (mismo patrón que LessonCard). Solo se muestra
          cuando hay un PDF de actividad; una vez enlazado, se sustituye
          por un badge informativo con el prefijo del bank_id. La creación
          del content y la subida del PDF siguen usando el flujo «Crear»
          existente — este botón solo añade el vínculo con FH. */}
      {!isStudent && hasActivityPdf && (
        <div className="mb-3">
          {hasBank ? (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
              <Link2 className="w-3 h-3 mr-1" /> bank_id: {item.bank_id.slice(0, 8)}…
            </Badge>
          ) : (
            <Button size="sm" variant="ghost" data-testid={`link-bank-btn-${item.id}`}
              onClick={async () => {
                const bid = window.prompt('Pega el bank_id de Feedback Hub para este PDF:');
                if (!bid) return;
                try {
                  await axios.post(`${API}/activities/content/${item.id}/link-bank`, { bank_id: bid.trim() }, headers);
                  toast.success('Enlazado a Feedback Hub');
                  onUpdated(item.id, { bank_id: bid.trim() });
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Error');
                }
              }} className="text-xs gap-1 text-slate-500">
              <Link2 className="w-3 h-3" /> Enlazar a Feedback Hub (bank_id)
            </Button>
          )}
        </div>
      )}

      {/* Fase 5 · Alumno: cuando el item está enlazado a un bank de FH
          usamos el mismo visor interactivo que Lecciones. Reglas:
          - Si `my_activity` existe (el alumno ya entregó una actividad
            FH para este content), mostrar el mismo lock verde que ya usa
            LessonCard.js. La corrección aparecerá en la pestaña
            «Correcciones» cuando el docente la publique.
          - Si no ha entregado aún, botón «Abrir actividad» que abre
            `PdfActivityDialog` (mismo componente que Lecciones). */}
      {isStudent && hasBank && hasActivityPdf && (
        <div className="mb-3">
          {item.my_activity ? (
            <div
              data-testid={`content-activity-submitted-${item.id}`}
              className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2"
            >
              <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Actividad entregada. El tutor revisará tu actividad y te enviará la corrección.
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              {activityFiles.map((f, i) => (
                <Button
                  key={f.filename || `open-${i}`}
                  onClick={() => setOpenActivityFile(f)}
                  data-testid={`open-activity-btn-${item.id}-${i}`}
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                >
                  <BookOpen className="w-4 h-4" /> Abrir actividad
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fase 5 · Ocultamos el flujo antiguo de subida de archivos para el
          alumno cuando el content está enlazado a Feedback Hub (`bank_id`
          presente). Todo el trabajo del alumno se hace ahora dentro del
          visor interactivo. StudentSubmitBox sigue existiendo intacto en
          el código para los items sin bank_id (backwards-compat) — solo
          se bloquea su render, tal como pidió el usuario. */}
      {isStudent && !hasBank && (
        <StudentSubmitBox
          item={item} contentType={contentType} submitLabel={submitLabel}
          showExternalCompletion={showExternalCompletion}
          t={t} token={token}
          onSubmitted={() => onUpdated(item.id, null)}
        />
      )}

      {!isStudent && (
        <div className="border-t pt-3 mt-3">
          <Button variant="ghost" size="sm" onClick={() => onReview(item.id)} data-testid={`review-btn-${item.id}`} className="gap-1 text-blue-600">
            <Eye className="w-4 h-4" /> {reviewLabel} ({item.submission_count || 0})
          </Button>
        </div>
      )}

      {/* Fase 5 · PdfActivityDialog compartido con Lecciones. Se monta
          en el árbol solo cuando el alumno abre la actividad. Cero
          cambios en PdfActivityViewer / PdfActivityDialog. */}
      <PdfActivityDialog
        open={!!openActivityFile}
        onClose={() => setOpenActivityFile(null)}
        contentId={item.id}
        pdfFile={openActivityFile}
        token={token}
        requireAudioBeforeSubmit={['task', 'reinforcement'].includes(item.content_type)}
        onActivitySubmitted={(data) => onUpdated(item.id, {
          my_activity: {
            activity_id: data?.activity_id,
            version: data?.version,
            submitted_at: new Date().toISOString(),
          },
        })}
      />
    </div>
  );
}
